import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useCompanyContext } from './use-company-context';

export interface FleetDocument {
  id: string;
  name: string;
  document_type: string;
  category: string;
  vehicle_id?: string;
  driver_id?: string;
  company_id: string;
  file_path?: string;
  file_size?: number;
  mime_type?: string;
  reference_number?: string;
  authority?: string;
  issue_date?: string;
  expiry_date?: string;
  status: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
  fleet_vehicles?: {
    plate: string;
    make?: string;
    model?: string;
  } | null;
  fleet_drivers?: {
    name: string;
    cpf?: string;
  } | null;
}

export function useFleetDocuments(companyIdParam?: string) {
  const { companyId: contextCompanyId } = useCompanyContext();
  const companyId = companyIdParam || contextCompanyId;
  
  console.log('🚚 [useFleetDocuments] companyId:', companyId);
  
  return useQuery({
    queryKey: ['fleet-documents', companyId],
    queryFn: async () => {
      if (!companyId) return [];
      
      const { data, error } = await supabase
        .from('fleet_documents')
        .select(`
          *,
          fleet_vehicles:vehicle_id (
            plate,
            make,
            model
          ),
          fleet_drivers:driver_id (
            name,
            cpf
          )
        `)
        .eq('company_id', companyId)
        .eq('status', 'active')
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map(item => ({
        ...item,
        fleet_vehicles: item.fleet_vehicles as any,
        fleet_drivers: item.fleet_drivers as any,
      })) as FleetDocument[];
    },
    enabled: !!companyId,
  });
}

export function useCreateFleetDocument(companyId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (documentData: Omit<FleetDocument, 'id' | 'created_at' | 'updated_at' | 'company_id' | 'fleet_vehicles' | 'fleet_drivers'>) => {
      const { data, error } = await supabase
        .from('fleet_documents')
        .insert({
          ...documentData,
          company_id: companyId,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-documents', companyId] });
      toast({
        title: "Documento criado",
        description: "Documento foi criado com sucesso.",
      });
    },
    onError: (error) => {
      console.error('Error creating document:', error);
      toast({
        variant: "destructive",
        title: "Erro ao criar documento",
        description: "Não foi possível criar o documento.",
      });
    },
  });
}

export function useUpdateFleetDocument(companyId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...documentData }: Partial<FleetDocument> & { id: string }) => {
      const { data, error } = await supabase
        .from('fleet_documents')
        .update(documentData)
        .eq('id', id)
        .eq('company_id', companyId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-documents', companyId] });
      toast({
        title: "Documento atualizado",
        description: "Documento foi atualizado com sucesso.",
      });
    },
    onError: (error) => {
      console.error('Error updating document:', error);
      toast({
        variant: "destructive",
        title: "Erro ao atualizar documento",
        description: "Não foi possível atualizar o documento.",
      });
    },
  });
}

export function useUploadDocument() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ file, path }: { file: File; path: string }) => {
      const { data, error } = await supabase.storage
        .from('fleet-documents')
        .upload(path, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) throw error;
      return data;
    },
    onError: (error) => {
      console.error('Error uploading file:', error);
      toast({
        variant: "destructive",
        title: "Erro ao fazer upload",
        description: "Não foi possível fazer upload do arquivo.",
      });
    },
  });
}

export function useDeleteFleetDocument(companyId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('fleet_documents')
        .delete()
        .eq('id', id)
        .eq('company_id', companyId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-documents', companyId] });
      toast({
        title: "Documento excluído",
        description: "Documento foi excluído com sucesso.",
      });
    },
    onError: (error) => {
      console.error('Error deleting document:', error);
      toast({
        variant: "destructive",
        title: "Erro ao excluir documento",
        description: "Não foi possível excluir o documento.",
      });
    },
  });
}